import { useState, type ElementType, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'

/** The 13 variants of DsTypography / DsTextVariant. */
export type DsTextVariant =
  | 'display' | 'title1' | 'title2' | 'title3'
  | 'body' | 'bodyStrong' | 'small' | 'smallStrong'
  | 'caption' | 'overline' | 'label' | 'numeric' | 'numericLarge'

export const textVariantClass: Record<DsTextVariant, string> = {
  display: 'font-display text-display',
  title1: 'font-display text-title1',
  title2: 'font-display text-title2',
  title3: 'text-title3',
  body: 'text-body',
  bodyStrong: 'text-body-strong',
  small: 'text-small',
  smallStrong: 'text-small-strong',
  caption: 'text-caption',
  overline: 'text-overline',
  label: 'text-label',
  numeric: 'text-numeric tabular-nums',
  numericLarge: 'text-numeric-large tabular-nums',
}

const defaultTag: Record<DsTextVariant, ElementType> = {
  display: 'h1', title1: 'h1', title2: 'h2', title3: 'h3',
  body: 'p', bodyStrong: 'p', small: 'p', smallStrong: 'p',
  caption: 'span', overline: 'span', label: 'span', numeric: 'span', numericLarge: 'span',
}

export interface DsTextProps {
  children: ReactNode
  variant?: DsTextVariant
  muted?: boolean
  tone?: DsTone
  maxLines?: number
  expandable?: boolean
  moreLabel?: string
  lessLabel?: string
  uppercase?: boolean
  align?: 'start' | 'center' | 'end'
  as?: ElementType
  className?: string
  id?: string
  /** Screen-reader text when the visible text is not enough. */
  semanticLabel?: string
}

const clampClass = (n: number) => ({ 1: 'line-clamp-1', 2: 'line-clamp-2', 3: 'line-clamp-3', 4: 'line-clamp-4', 5: 'line-clamp-5', 6: 'line-clamp-6' })[n] ?? 'line-clamp-6'

export function DsText({ children, variant = 'body', muted, tone, maxLines, expandable, moreLabel, lessLabel, uppercase, align, as, className, id, semanticLabel }: DsTextProps) {
  const [expanded, setExpanded] = useState(false)
  const Tag = as ?? defaultTag[variant]
  const colour = tone ? toneClass[tone].text : muted ? 'text-muted-foreground' : 'text-foreground'
  const clamped = maxLines && !(expandable && expanded)
  return (
    <>
      <Tag
        id={id}
        aria-label={semanticLabel}
        className={cn(textVariantClass[variant], colour, clamped && clampClass(maxLines), uppercase && 'uppercase', align === 'center' && 'text-center', align === 'end' && 'text-right', className)}
      >
        {children}
      </Tag>
      {expandable && maxLines && (
        <button type="button" onClick={() => setExpanded((e) => !e)} className="text-small-strong text-link underline-offset-4 hover:underline">
          {expanded ? lessLabel ?? 'Less' : moreLabel ?? 'More'}
        </button>
      )}
    </>
  )
}
